import React, { useCallback, useEffect, useState } from "react";
import "./Vacancies.css";

import NavBar from "../components/NavBar/Navigation";
import Footer from "../components/Footer/Footer";
import VacanciesBanner from "../assets/vacancies.png";
import pgLogo from "../assets/pglogo.png";

/* =========================================================
   MONDAY.COM CONFIGURATION
   ========================================================= */

const MONDAY_API_URL = "https://api.monday.com/v2";

const API_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;
const BOARD_ID = process.env.REACT_APP_MONDAY_VACANCIES_BOARD_ID;

const RECRUITMENT_EMAIL = "recruitment.pg@projectgreen.mt";

/*
  If true, vacancies whose Closing Date has passed disappear.
  Set to false to let only the Archive column control visibility.
*/
const HIDE_EXPIRED_VACANCIES = true;

/* Timezone used for the issue date / timestamp */
const MALTA_TZ = "Europe/Malta";

/* =========================================================
   DEPARTMENT COLOURS
   ========================================================= */

const departmentColors = {
  "Park Management & Servicing": { from: "#006a53", to: "#005145", accent: "#dedc00" },
  "Parks Management & Servicing": { from: "#006a53", to: "#005145", accent: "#dedc00" },
  "Parks Management and Servicing": { from: "#006a53", to: "#005145", accent: "#dedc00" },
  Procurement: { from: "#f39200", to: "#b96b00", accent: "#dedc00" },
  "Procurement & Stores": { from: "#005145", to: "#00332c", accent: "#f192b0" },
  "People & Culture": { from: "#f192b0", to: "#c25f80", accent: "#dedc00" },
  Operations: { from: "#dedc00", to: "#a3a200", accent: "#005145" },
  Finance: { from: "#005145", to: "#00332c", accent: "#dedc00" },
  ICT: { from: "#006a53", to: "#005145", accent: "#f192b0" },
  IT: { from: "#006a53", to: "#005145", accent: "#f192b0" },
  "Marketing & Internal Affairs": { from: "#f192b0", to: "#c25f80", accent: "#dedc00" },
  Communications: { from: "#f192b0", to: "#c25f80", accent: "#dedc00" },
  Default: { from: "#006a53", to: "#005145", accent: "#dedc00" },
};

const getColor = (department) => {
  if (!department) return departmentColors.Default;
  return departmentColors[department] || departmentColors.Default;
};

/* =========================================================
   DATE HELPERS
   ========================================================= */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const parseDate = (value) => {
  if (!value) return null;

  const cleanValue = String(value).trim();
  const dateOnlyMatch = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(cleanValue);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return value || "";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const formatShortDate = (value) => {
  const date = parseDate(value);
  if (!date) return value || "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${day}/${month}/${date.getFullYear()}`;
};

const daysLeft = (value) => {
  const closingDate = parseDate(value);
  if (!closingDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  closingDate.setHours(0, 0, 0, 0);

  return Math.round((closingDate.getTime() - today.getTime()) / 86400000);
};

/* =========================================================
   ISSUE DATE / TIMESTAMP HELPERS (Malta time)

   Monday returns created_at as an ISO timestamp in UTC,
   e.g. "2026-10-13T12:32:05Z". It is converted to Malta
   time so the date and time are exact for staff.
   ========================================================= */

const getTimeParts = (value) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: MALTA_TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value;

  return {
    year: get("year"),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

/* Same format as the closing date: 13 October 2026 */
const formatIssueDate = (value) => {
  const p = getTimeParts(value);
  if (!p) return "";
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year}`;
};

/* 13 October 2026 at 14:32:05 */
const formatTimestamp = (value) => {
  const p = getTimeParts(value);
  if (!p) return "";
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year} at ${p.hour}:${p.minute}:${p.second}`;
};

/* =========================================================
   TEXT / COLUMN HELPERS
   ========================================================= */

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const normalizeColumnTitle = (value) =>
  normalizeText(value).replace(/\s+/g, " ").trim();

const findColumn = (columns, names) => {
  if (!Array.isArray(columns)) return null;

  const normalizedNames = names.map((name) => normalizeColumnTitle(name));

  return (
    columns.find((column) => {
      const title = normalizeColumnTitle(column?.column?.title);
      return normalizedNames.includes(title);
    }) || null
  );
};

const getColumnText = (columns, ...names) => {
  const column = findColumn(columns, names);
  return String(column?.text || "").trim();
};

/* =========================================================
   MULTI-COLUMN LONG TEXT SUPPORT
   (About the role, About the role 1, About the role 2 ...)
   ========================================================= */

const extractNumberFromTitle = (title) => {
  const match = String(title || "").match(/(\d+)\s*$/);
  if (!match) return 0;
  return Number(match[1]);
};

const getColumnsStartingWith = (columns, prefixes) => {
  if (!Array.isArray(columns)) return [];

  const normalizedPrefixes = prefixes.map((prefix) => normalizeColumnTitle(prefix));

  return columns
    .filter((column) => {
      const title = normalizeColumnTitle(column?.column?.title);

      return normalizedPrefixes.some(
        (prefix) =>
          title === prefix ||
          title.startsWith(`${prefix} `) ||
          title.startsWith(`${prefix}-`) ||
          title.startsWith(`${prefix} -`)
      );
    })
    .sort(
      (a, b) =>
        extractNumberFromTitle(a?.column?.title) -
        extractNumberFromTitle(b?.column?.title)
    );
};

const getCombinedColumnText = (columns, prefixes) => {
  return getColumnsStartingWith(columns, prefixes)
    .map((column) => String(column?.text || "").trim())
    .filter(Boolean)
    .join("\n");
};

/* =========================================================
   TEXT TO BULLET LIST
   ========================================================= */

const textToList = (value) => {
  if (!value) return [];

  return String(value)
    .split(/\r?\n+/)
    .map((line) => line.replace(/^\s*(?:[-•▪◦*]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);
};

/* =========================================================
   CARD DESCRIPTION
   ========================================================= */

const makeCardDescription = (description, aboutRoleText) => {
  if (description) return description;
  if (!aboutRoleText) return "";

  const clean = String(aboutRoleText)
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const MAX_LENGTH = 230;

  if (clean.length <= MAX_LENGTH) return clean;

  return `${clean.substring(0, MAX_LENGTH)}...`;
};

/* =========================================================
   ARCHIVE COLUMN

   The "Archive" column on the board is a BUTTON column.
   A button column's text is always its label ("Archive"),
   so it must never be read as data.

   Only checkbox / status style columns are used to decide
   whether a vacancy is archived.
   ========================================================= */

const isArchived = (columns) => {
  const archiveColumn = findColumn(columns, ["Archive", "Archived"]);

  if (!archiveColumn) return false;

  const type = String(archiveColumn.type || "").toLowerCase();

  /* Button columns only hold a label, never a state. */
  if (type === "button") return false;

  /* Checkbox column: raw value is {"checked":"true"} */
  if (archiveColumn.value) {
    try {
      const parsed = JSON.parse(archiveColumn.value);

      if (parsed?.checked === true || parsed?.checked === "true") {
        return true;
      }
    } catch (error) {
      /* Ignore invalid JSON and check the text below. */
    }
  }

  /* Status / text column fallback */
  const value = normalizeText(archiveColumn.text);

  const archivedValues = ["yes", "true", "closed", "done", "checked", "1", "v", "✓"];

  return archivedValues.includes(value);
};

/* =========================================================
   PASS MARK
   ========================================================= */

const formatPassMark = (value) => {
  const clean = String(value || "60").trim();
  if (clean.endsWith("%")) return clean;
  return `${clean}%`;
};

/* =========================================================
   PDF GENERATION

   Built on the fly in the browser when the Download PDF
   button is pressed. Nothing is pre-generated.
   jsPDF is loaded only at that moment.
   ========================================================= */

const hexToRgb = (hex) => {
  const clean = String(hex).replace("#", "");
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16),
  ];
};

const lerpColor = (a, b, t) => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

/*
  The built-in PDF font (Helvetica) only supports Latin-1.
  This converts anything outside that range to a safe equivalent
  so no garbled characters appear in the PDF.
*/
const pdfSafe = (value) => {
  const replaced = String(value || "")
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u25AA\u25E6\u00B7]/g, "-")
    .replace(/[\u00A0\u2007\u202F]/g, " ")
    .replace(/\u0126/g, "H")
    .replace(/\u0127/g, "h");

  return Array.from(replaced)
    .map((char) => {
      const code = char.charCodeAt(0);

      if (char === "\n" || (code >= 32 && code <= 126) || (code >= 160 && code <= 255)) {
        return char;
      }

      const stripped = char.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const strippedCode = stripped.charCodeAt(0);

      if (stripped.length === 1 && strippedCode >= 32 && strippedCode <= 126) {
        return stripped;
      }

      return "";
    })
    .join("");
};

const loadImageForPdf = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        canvas.getContext("2d").drawImage(image, 0, 0);

        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      } catch (error) {
        resolve(null);
      }
    };

    image.onerror = () => resolve(null);
    image.src = src;
  });

const generateVacancyPdf = async (vacancy) => {
  const { jsPDF } = await import("jspdf");

  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

  const FONT = "helvetica";
  const PAGE_W = 210;
  const PAGE_H = 297;
  const M = 18;
  const CW = PAGE_W - M * 2;
  const NEXT_PAGE_TOP = 26;
  const BOTTOM = PAGE_H - 20;
  const ISSUER_TOP = PAGE_H - 44;

  const colors = getColor(vacancy.department);
  const DEPT = hexToRgb(colors.from);

  const DARK = [0, 81, 69];
  const GREEN = [0, 106, 83];
  const LIME = [222, 220, 0];
  const CREAM = [247, 248, 240];
  const BODY = [85, 99, 95];
  const LEAD = [63, 77, 73];
  const MUTED = [124, 138, 133];
  const SUBTLE = [190, 217, 210];
  const BORDER = [226, 230, 220];

  const generatedAt = new Date();
  const issuedTimestamp = vacancy.issuedAt
    ? `${formatTimestamp(vacancy.issuedAt)} (Malta time)`
    : "Not available";

  const logo = await loadImageForPdf(pgLogo);

  const STRIPS = 60;
  const stripW = PAGE_W / STRIPS;

  const drawBand = (height) => {
    for (let i = 0; i < STRIPS; i += 1) {
      const color = lerpColor(DARK, GREEN, i / (STRIPS - 1));
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(i * stripW, 0, stripW + 0.4, height, "F");
    }
  };

  /* ---------------------------------------------------
     PAGE 1 HEADER BAND
     --------------------------------------------------- */

  const BAND_H = 36;

  drawBand(BAND_H);

  doc.setFillColor(LIME[0], LIME[1], LIME[2]);
  doc.rect(0, BAND_H, PAGE_W, 1.4, "F");

  if (logo) {
    const maxW = 44;
    const maxH = 17;
    const ratio = logo.width / logo.height;

    let w = maxW;
    let h = w / ratio;

    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }

    doc.addImage(logo.dataUrl, "PNG", M, (BAND_H - h) / 2, w, h);
  } else {
    doc.setFont(FONT, "bold");
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text("PROJECT GREEN", M, BAND_H / 2 + 2);
  }

  doc.setFont(FONT, "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text("INTERNAL CALL FOR APPLICATIONS", PAGE_W - M, BAND_H / 2 - 1, {
    align: "right",
  });

  doc.setFont(FONT, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(SUBTLE[0], SUBTLE[1], SUBTLE[2]);
  doc.text(`Vacancy Notice  |  Ref: ${pdfSafe(vacancy.id)}`, PAGE_W - M, BAND_H / 2 + 4.5, {
    align: "right",
  });

  /* ---------------------------------------------------
     TITLE
     --------------------------------------------------- */

  let y = BAND_H + 18;

  doc.setFont(FONT, "bold");
  doc.setFontSize(22);
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);

  const titleLines = doc.splitTextToSize(pdfSafe(vacancy.title), CW);
  const TITLE_LH = 9.2;

  titleLines.forEach((line, index) => {
    doc.text(line, M, y + index * TITLE_LH);
  });

  y += (titleLines.length - 1) * TITLE_LH + 6;

  doc.setFillColor(DEPT[0], DEPT[1], DEPT[2]);
  doc.roundedRect(M, y, 24, 1.3, 0.6, 0.6, "F");

  y += 9;

  /* ---------------------------------------------------
     DETAILS GRID
     --------------------------------------------------- */

  const COLS = 3;
  const COL_W = CW / COLS;

  doc.setFont(FONT, "bold");
  doc.setFontSize(10);

  const cells = [
    ["Department", vacancy.department],
    ["Grade", vacancy.grade],
    ["Positions", vacancy.positions],
    ["Reports to", vacancy.reportsTo],
    ["Closing date", vacancy.endDate ? formatDate(vacancy.endDate) : ""],
    ["Pass mark", formatPassMark(vacancy.passMark)],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => ({
      label: pdfSafe(label).toUpperCase(),
      lines: doc.splitTextToSize(pdfSafe(value), COL_W - 10),
    }));

  const gridRows = [];
  for (let i = 0; i < cells.length; i += COLS) {
    const row = cells.slice(i, i + COLS);
    const maxLines = Math.max(...row.map((cell) => cell.lines.length));
    gridRows.push({ cells: row, height: 12 + (maxLines - 1) * 4.6 + 5.5 });
  }

  const gridH = gridRows.reduce((sum, row) => sum + row.height, 0);

  doc.setFillColor(CREAM[0], CREAM[1], CREAM[2]);
  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, y, CW, gridH, 2, 2, "FD");

  let rowTop = y;
  gridRows.forEach((row, rowIndex) => {
    if (rowIndex > 0) {
      doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
      doc.setLineWidth(0.3);
      doc.line(M, rowTop, M + CW, rowTop);
    }

    row.cells.forEach((cell, index) => {
      const x = M + index * COL_W + 5;

      doc.setFont(FONT, "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
      doc.text(cell.label, x, rowTop + 6);

      doc.setFont(FONT, "bold");
      doc.setFontSize(10);
      doc.setTextColor(DARK[0], DARK[1], DARK[2]);
      cell.lines.forEach((line, lineIndex) => {
        doc.text(line, x, rowTop + 12 + lineIndex * 4.6);
      });
    });

    rowTop += row.height;
  });

  if (gridRows.length > 0) {
    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.3);
    for (let c = 1; c < COLS; c += 1) {
      doc.line(M + c * COL_W, y, M + c * COL_W, y + gridH);
    }
  }

  y += gridH + 7;

  /* ---------------------------------------------------
     ISSUE DATE & TIME BOX
     --------------------------------------------------- */

  const ISSUE_H = 18;

  doc.setFillColor(CREAM[0], CREAM[1], CREAM[2]);
  doc.roundedRect(M, y, CW, ISSUE_H, 2, 2, "F");
  doc.setFillColor(LIME[0], LIME[1], LIME[2]);
  doc.rect(M, y, 1.6, ISSUE_H, "F");

  doc.setFont(FONT, "bold");
  doc.setFontSize(7);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text("ISSUE DATE & TIME", M + 7, y + 6.5);

  doc.setFont(FONT, "bold");
  doc.setFontSize(12);
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text(pdfSafe(issuedTimestamp), M + 7, y + 13.2);

  y += ISSUE_H + 10;

  /* ---------------------------------------------------
     PAGINATION HELPERS
     --------------------------------------------------- */

  const ensureSpace = (needed) => {
    if (y + needed > BOTTOM) {
      doc.addPage();
      y = NEXT_PAGE_TOP;
    }
  };

  /* ---------------------------------------------------
     INTRO PARAGRAPHS
     --------------------------------------------------- */

  const introParagraphs = String(vacancy.description || "")
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter((line) => line && !/^[-\u2022\u25AA\u25E6*\u2013\u2014]+$/.test(line));

  introParagraphs.forEach((paragraph) => {
    doc.setFont(FONT, "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(LEAD[0], LEAD[1], LEAD[2]);

    const lines = doc.splitTextToSize(pdfSafe(paragraph), CW);

    lines.forEach((line) => {
      ensureSpace(5.8);
      doc.setFont(FONT, "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(LEAD[0], LEAD[1], LEAD[2]);
      doc.text(line, M, y);
      y += 5.8;
    });

    y += 2.5;
  });

  if (introParagraphs.length > 0) {
    y += 4;
  }

  /* ---------------------------------------------------
     SECTIONS WITH BULLETS (long bullets split across pages)
     --------------------------------------------------- */

  const BULLET_LH = 4.8;

  const drawSection = (title, items) => {
    if (!items || items.length === 0) return;

    ensureSpace(26);

    doc.setFont(FONT, "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(GREEN[0], GREEN[1], GREEN[2]);
    doc.text(title.toUpperCase(), M, y);

    y += 3;
    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.3);
    doc.line(M, y, M + CW, y);
    y += 6.5;

    items.forEach((item) => {
      doc.setFont(FONT, "normal");
      doc.setFontSize(9.5);

      const lines = doc.splitTextToSize(pdfSafe(item), CW - 8);

      /* Keep at least the first two lines together */
      ensureSpace(Math.min(lines.length, 2) * BULLET_LH + 1);

      doc.setFillColor(DEPT[0], DEPT[1], DEPT[2]);
      doc.roundedRect(M + 0.5, y - 2.6, 2.4, 2.4, 0.5, 0.5, "F");

      lines.forEach((line, index) => {
        if (index > 0) {
          ensureSpace(BULLET_LH);
        }

        doc.setFont(FONT, "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(BODY[0], BODY[1], BODY[2]);
        doc.text(line, M + 7, y);

        y += BULLET_LH;
      });

      y += 2.4;
    });

    y += 5;
  };

  drawSection("About the role", vacancy.duties);
  drawSection("About you", vacancy.requirements);

  /* ---------------------------------------------------
     PASS MARK NOTE
     --------------------------------------------------- */

  const noteText = pdfSafe(
    `The pass mark for this post is ${formatPassMark(vacancy.passMark)} and the Interview Report will remain valid for one (1) year from date of issue.`
  );

  doc.setFont(FONT, "normal");
  doc.setFontSize(8.8);
  const noteLines = doc.splitTextToSize(noteText, CW - 14);
  const noteH = noteLines.length * 4.6 + 9;

  ensureSpace(noteH + 4);

  doc.setFillColor(CREAM[0], CREAM[1], CREAM[2]);
  doc.roundedRect(M, y, CW, noteH, 2, 2, "F");
  doc.setFillColor(LIME[0], LIME[1], LIME[2]);
  doc.rect(M, y, 1.4, noteH, "F");

  doc.setFont(FONT, "normal");
  doc.setFontSize(8.8);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  noteLines.forEach((line, index) => {
    doc.text(line, M + 7, y + 6.5 + index * 4.6);
  });

  y += noteH + 9;

  /* ---------------------------------------------------
     HOW TO APPLY
     --------------------------------------------------- */

  ensureSpace(36);

  doc.setFont(FONT, "bold");
  doc.setFontSize(10);
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text(
    vacancy.endDate
      ? `Applications close ${pdfSafe(formatDate(vacancy.endDate))}`
      : "Applications currently open",
    M,
    y
  );

  y += 6;

  doc.setFont(FONT, "normal");
  doc.setFontSize(9);
  doc.setTextColor(BODY[0], BODY[1], BODY[2]);

  const applyLines = doc.splitTextToSize(
    "Applications, together with a full CV and copies of relevant qualifications, are to be sent by email by the closing date.",
    CW
  );
  applyLines.forEach((line) => {
    doc.text(line, M, y);
    y += 4.6;
  });

  y += 3;

  const BTN_H = 11;
  const btnLabel = `Apply: ${RECRUITMENT_EMAIL}`;

  doc.setFont(FONT, "bold");
  doc.setFontSize(9.5);
  const btnW = doc.getTextWidth(btnLabel) + 16;

  doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
  doc.roundedRect(M, y, btnW, BTN_H, 5.5, 5.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(btnLabel, M + 8, y + 6.9);

  doc.link(M, y, btnW, BTN_H, {
    url: `mailto:${RECRUITMENT_EMAIL}?subject=${encodeURIComponent(
      `Application - ${vacancy.title}`
    )}`,
  });

  y += BTN_H;

  /* If the content runs into the "Issued by" area, move it to a new page */
  const hasIssuer = Boolean(vacancy.issuedBy);

  if (hasIssuer && y > ISSUER_TOP - 6) {
    doc.addPage();
  }

  /* ---------------------------------------------------
     RUNNING HEADER + FOOTER ON EVERY PAGE
     --------------------------------------------------- */

  const totalPages = doc.getNumberOfPages();
  const generatedStamp = formatTimestamp(generatedAt);

  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);

    if (page > 1) {
      drawBand(11);

      doc.setFillColor(LIME[0], LIME[1], LIME[2]);
      doc.rect(0, 11, PAGE_W, 0.8, "F");

      doc.setFont(FONT, "bold");
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);

      const runningTitle = doc.splitTextToSize(pdfSafe(vacancy.title), CW - 50)[0];
      doc.text(runningTitle, M, 7.2);

      doc.setFont(FONT, "normal");
      doc.setTextColor(SUBTLE[0], SUBTLE[1], SUBTLE[2]);
      doc.text(`Issued ${pdfSafe(formatIssueDate(vacancy.issuedAt))}`, PAGE_W - M, 7.2, {
        align: "right",
      });
    }

    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.3);
    doc.line(M, PAGE_H - 14, PAGE_W - M, PAGE_H - 14);

    doc.setFont(FONT, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text(
      `Project Green - Internal Call for Applications  |  Generated ${pdfSafe(generatedStamp)}`,
      M,
      PAGE_H - 8.5
    );
    doc.text(`Page ${page} of ${totalPages}`, PAGE_W - M, PAGE_H - 8.5, {
      align: "right",
    });
  }

  /* ---------------------------------------------------
     ISSUED BY (bottom of the last page)
     --------------------------------------------------- */

  if (hasIssuer) {
    doc.setPage(totalPages);

    doc.setDrawColor(DARK[0], DARK[1], DARK[2]);
    doc.setLineWidth(0.5);
    doc.line(M, ISSUER_TOP, M + CW, ISSUER_TOP);

    doc.setFont(FONT, "bold");
    doc.setFontSize(7);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text("ISSUED BY", M, ISSUER_TOP + 7);

    doc.setFont(FONT, "bold");
    doc.setFontSize(11);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text(pdfSafe(vacancy.issuedBy), M, ISSUER_TOP + 13);

    if (vacancy.issuedByEmail) {
      doc.setFont(FONT, "normal");
      doc.setFontSize(9);
      doc.setTextColor(GREEN[0], GREEN[1], GREEN[2]);
      doc.textWithLink(pdfSafe(vacancy.issuedByEmail), M, ISSUER_TOP + 18.5, {
        url: `mailto:${vacancy.issuedByEmail}`,
      });
    }

    doc.setFont(FONT, "bold");
    doc.setFontSize(7);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text("ISSUE DATE & TIME", PAGE_W - M, ISSUER_TOP + 7, { align: "right" });

    doc.setFont(FONT, "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text(pdfSafe(issuedTimestamp), PAGE_W - M, ISSUER_TOP + 13, {
      align: "right",
    });
  }

  const fileName = `Vacancy - ${pdfSafe(vacancy.title)
    .replace(/[\\/:*?"<>|]/g, "")
    .trim()}.pdf`;

  doc.save(fileName);
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const Vacancies = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pdfBusyId, setPdfBusyId] = useState(null);

  /* =======================================================
     GET VACANCIES FROM MONDAY.COM
     ======================================================= */

  useEffect(() => {
    let cancelled = false;

    const buildQuery = (includeCreator) => `
      query {
        boards(ids: [${BOARD_ID}]) {
          items_page(limit: 100) {
            items {
              id
              name
              created_at
              ${includeCreator ? "creator { id name email }" : ""}
              column_values {
                id
                type
                text
                value
                column {
                  title
                }
              }
            }
          }
        }
      }
    `;

    const runQuery = async (query) => {
      const response = await fetch(MONDAY_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: API_TOKEN,
        },
        body: JSON.stringify({ query }),
      });

      const data = await response.json();

      return { response, data };
    };

    const loadVacancies = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!API_TOKEN) {
          throw new Error("REACT_APP_MONDAY_TOKEN is missing from .env");
        }

        if (!BOARD_ID) {
          throw new Error(
            "REACT_APP_MONDAY_VACANCIES_BOARD_ID is missing from .env"
          );
        }

        /*
          First try with the creator (name + email).
          If the token is not allowed to read user details,
          retry without it so the page still works.
        */
        let { response, data } = await runQuery(buildQuery(true));

        if (data?.errors && data.errors.length > 0) {
          console.warn(
            "VACANCIES: creator lookup failed, retrying without it",
            data.errors
          );

          ({ response, data } = await runQuery(buildQuery(false)));
        }

        if (!response.ok) {
          throw new Error(`Monday API error: ${response.status}`);
        }

        if (data?.errors && data.errors.length > 0) {
          throw new Error(
            data.errors.map((apiError) => apiError.message).join(", ")
          );
        }

        const items = data?.data?.boards?.[0]?.items_page?.items || [];

        console.log(`VACANCIES: ${items.length} item(s) returned by Monday`);

        const parsedVacancies = items
          .map((item) => {
            const columns = item.column_values || [];

            const department = getColumnText(columns, "Department");
            const grade = getColumnText(columns, "Grade");
            const positions = getColumnText(columns, "Positions", "Number of Positions");
            const reportsTo = getColumnText(columns, "Reports To", "Reports to");
            const description = getColumnText(columns, "Description", "Short Description");

            const aboutRoleText = getCombinedColumnText(columns, [
              "About the role",
              "Duties",
            ]);

            /*
              "Requirements" is not included here because the board has
              both "Requirements" and "About you" with the same text,
              which made every bullet appear twice.
            */
            const aboutYouText = getCombinedColumnText(columns, ["About you"]);

            const closingDate = getColumnText(columns, "Closing Date", "End Date");
            const passMark = getColumnText(columns, "Pass Mark", "Pass mark") || "60";

            const archived = isArchived(columns);

            return {
              id: item.id,
              title: item.name,
              issuedAt: item.created_at || "",
              issuedBy: item.creator?.name || "",
              issuedByEmail: item.creator?.email || "",
              department,
              grade,
              positions,
              reportsTo,
              description: makeCardDescription(description, aboutRoleText),
              aboutRoleText,
              aboutYouText,
              duties: textToList(aboutRoleText),
              requirements: textToList(aboutYouText),
              endDate: closingDate,
              passMark,
              archived,
            };
          })

          /* Remove untitled and archived vacancies */
          .filter((vacancy) => {
            if (!vacancy.title) {
              console.log("VACANCIES: hidden (no title)", vacancy.id);
              return false;
            }

            if (vacancy.archived) {
              console.log(
                `VACANCIES: hidden "${vacancy.title}" because Archive column is set`
              );
              return false;
            }

            return true;
          })

          /* Remove expired vacancies */
          .filter((vacancy) => {
            if (!HIDE_EXPIRED_VACANCIES) return true;
            if (!vacancy.endDate) return true;

            const remaining = daysLeft(vacancy.endDate);

            if (remaining === null) return true;

            if (remaining < 0) {
              console.log(
                `VACANCIES: hidden "${vacancy.title}" because closing date has passed`
              );
              return false;
            }

            return true;
          })

          /* Sort by closing date, earliest first */
          .sort((a, b) => {
            const dateA = parseDate(a.endDate);
            const dateB = parseDate(b.endDate);

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            return dateA.getTime() - dateB.getTime();
          });

        if (!cancelled) {
          setVacancies(parsedVacancies);
        }
      } catch (err) {
        console.error("FAILED TO LOAD MONDAY VACANCIES:", err);

        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadVacancies();

    return () => {
      cancelled = true;
    };
  }, []);

  /* =======================================================
     CLOSE MODAL
     ======================================================= */

  const closeModal = useCallback(() => {
    setSelected(null);
  }, []);

  useEffect(() => {
    if (!selected) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [selected, closeModal]);

  /* =======================================================
     APPLICATION EMAIL
     ======================================================= */

  const mailtoFor = (vacancy) => {
    const subject = encodeURIComponent(`Application – ${vacancy.title}`);
    return `mailto:${RECRUITMENT_EMAIL}?subject=${subject}`;
  };

  /* =======================================================
     DOWNLOAD PDF (generated when the button is pressed)
     ======================================================= */

  const handleDownloadPdf = async (vacancy, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (pdfBusyId) return;

    try {
      setPdfBusyId(vacancy.id);
      await generateVacancyPdf(vacancy);
    } catch (err) {
      console.error("FAILED TO CREATE VACANCY PDF:", err);
      window.alert("Sorry, the PDF could not be created. Please try again.");
    } finally {
      setPdfBusyId(null);
    }
  };

  const renderPdfIcon = () => (
    <svg
      className="vacancy-pdf-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  );

  /* =======================================================
     WEBSITE
     ======================================================= */

  return (
    <div className="vacancies-page">
      <NavBar />

      {/* PAGE BANNER */}
      <div
        className="vacancies-banner"
        style={{ backgroundImage: `url(${VacanciesBanner})` }}
      >
        <div className="vacancies-banner-overlay" />

        <div className="vacancies-banner-text">
          <h2 className="vacancies-banner-heading">Vacancies</h2>
          <p className="vacancies-banner-sub">Explore Our Internal Calls</p>
        </div>
      </div>

      {/* MAIN PAGE CONTENT */}
      <div className="vacancies-container">
        <div className="vacancies-intro">
          <span className="vacancies-eyebrow">Now open</span>

          <h2 className="vacancies-heading">
            Our Internal Call for Applications
          </h2>

          <p className="vacancies-lead">
            Grow with Project Green. Applications, together with a full CV and
            copies of relevant qualifications, are to be sent to{" "}
            <a href={`mailto:${RECRUITMENT_EMAIL}`}>{RECRUITMENT_EMAIL}</a> by
            the closing date shown on each call.
          </p>
        </div>

        {/* LOADING */}
        {loading && <p className="vacancies-status">Loading vacancies...</p>}

        {/* ERROR */}
        {!loading && error && (
          <div className="vacancies-status-wrapper">
            <p className="vacancies-status error">Failed to load vacancies.</p>
            <p className="vacancies-error-detail">{error.message}</p>
          </div>
        )}

        {/* NO VACANCIES */}
        {!loading && !error && vacancies.length === 0 && (
          <p className="vacancies-status">
            There are currently no open vacancies.
          </p>
        )}

        {/* VACANCY CARDS */}
        {!loading && !error && vacancies.length > 0 && (
          <div className="vacancies-grid">
            {vacancies.map((vacancy) => {
              const color = getColor(vacancy.department);
              const left = daysLeft(vacancy.endDate);
              const isBusy = pdfBusyId === vacancy.id;

              return (
                <article
                  className="vacancy-card"
                  key={vacancy.id}
                  onClick={() => setSelected(vacancy)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelected(vacancy);
                    }
                  }}
                  style={{
                    "--card-from": color.from,
                    "--card-to": color.to,
                    "--card-accent": color.accent,
                  }}
                >
                  {/* COLOURED CARD HEADER */}
                  <div className="vacancy-cover">
                    <div className="vacancy-cover-pattern" />

                    <span className="vacancy-grade-mark">
                      {vacancy.grade || "PG"}
                    </span>

                    <div className="vacancy-cover-top">
                      {vacancy.department && (
                        <span className="vacancy-dept-badge">
                          {vacancy.department}
                        </span>
                      )}

                      {left !== null && left >= 0 && (
                        <span className="vacancy-days-badge">
                          {left === 0 ? "Closes today" : `${left} days left`}
                        </span>
                      )}
                    </div>

                    <div className="vacancy-cover-bottom">
                      <h3 className="vacancy-title">{vacancy.title}</h3>

                      <div className="vacancy-meta">
                        {vacancy.grade && <span>Grade {vacancy.grade}</span>}

                        {vacancy.positions && (
                          <span>
                            {vacancy.positions}{" "}
                            {String(vacancy.positions) === "1"
                              ? "position"
                              : "positions"}
                          </span>
                        )}
                      </div>

                      <span className="vacancy-cta">Find out more</span>
                    </div>
                  </div>

                  {/* WHITE CARD BODY */}
                  <div className="vacancy-body">
                    {vacancy.description && (
                      <p className="vacancy-description">
                        {vacancy.description}
                      </p>
                    )}

                    {vacancy.endDate && (
                      <div className="vacancy-footer">
                        <span className="vacancy-date-label">Closing date</span>
                        <span className="vacancy-date">
                          {formatShortDate(vacancy.endDate)}
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="vacancy-pdf-btn"
                      disabled={Boolean(pdfBusyId)}
                      onClick={(event) => handleDownloadPdf(vacancy, event)}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {renderPdfIcon()}
                      {isBusy ? "Creating PDF..." : "Download PDF"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* VACANCY POPUP */}
      {selected && (
        <div className="vacancy-modal-overlay" onClick={closeModal}>
          <div
            className="vacancy-modal"
            onClick={(event) => event.stopPropagation()}
            style={{
              "--card-from": getColor(selected.department).from,
              "--card-to": getColor(selected.department).to,
              "--card-accent": getColor(selected.department).accent,
            }}
          >
            <button
              type="button"
              className="vacancy-modal-close"
              onClick={closeModal}
              aria-label="Close vacancy"
            >
              &times;
            </button>

            {/* MODAL HEADER */}
            <div className="vacancy-modal-header">
              {selected.department && (
                <span className="vacancy-dept-badge">{selected.department}</span>
              )}

              <h3>{selected.title}</h3>

              <div className="vacancy-modal-facts">
                {selected.grade && (
                  <span>
                    <strong>Grade</strong>
                    {selected.grade}
                  </span>
                )}

                {selected.positions && (
                  <span>
                    <strong>Positions</strong>
                    {selected.positions}
                  </span>
                )}

                {selected.reportsTo && (
                  <span>
                    <strong>Reports to</strong>
                    {selected.reportsTo}
                  </span>
                )}

                {selected.issuedAt && (
                  <span>
                    <strong>Issued</strong>
                    {formatIssueDate(selected.issuedAt)}
                  </span>
                )}

                {selected.endDate && (
                  <span>
                    <strong>Closing</strong>
                    {formatDate(selected.endDate)}
                  </span>
                )}
              </div>
            </div>

            {/* SCROLLABLE MODAL CONTENT */}
            <div className="vacancy-modal-body">
              {selected.description && (
                <p className="vacancy-modal-lead">{selected.description}</p>
              )}

              {selected.duties && selected.duties.length > 0 && (
                <section className="vacancy-modal-section">
                  <h4>About the role</h4>

                  <ul>
                    {selected.duties.map((duty, index) => (
                      <li key={`duty-${index}`}>{duty}</li>
                    ))}
                  </ul>
                </section>
              )}

              {selected.requirements && selected.requirements.length > 0 && (
                <section className="vacancy-modal-section">
                  <h4>About you</h4>

                  <ul>
                    {selected.requirements.map((requirement, index) => (
                      <li key={`requirement-${index}`}>{requirement}</li>
                    ))}
                  </ul>
                </section>
              )}

              <p className="vacancy-modal-note">
                The pass mark for this post is {formatPassMark(selected.passMark)}{" "}
                and the Interview Report will remain valid for one (1) year
                from date of issue.
              </p>
            </div>

            {/* MODAL FOOTER */}
            <div className="vacancy-modal-footer">
              <span className="vacancy-modal-deadline">
                {selected.endDate
                  ? `Applications close ${formatDate(selected.endDate)}`
                  : "Applications currently open"}
              </span>

              <div className="vacancy-modal-actions">
                <button
                  type="button"
                  className="vacancy-pdf-btn vacancy-pdf-btn-modal"
                  disabled={Boolean(pdfBusyId)}
                  onClick={(event) => handleDownloadPdf(selected, event)}
                >
                  {renderPdfIcon()}
                  {pdfBusyId === selected.id ? "Creating PDF..." : "Download PDF"}
                </button>

                <a className="vacancy-apply-btn" href={mailtoFor(selected)}>
                  Apply Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Vacancies;